import { FolderOpenOutlined } from "@ant-design/icons"
import { Button, Form } from "antd"
import { uniq } from "lodash"
import { useCallback, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { classAtoms, portfolioAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import { ensurePortfolioExists } from "#components/PortfolioManagement/ensurePortfolioExists"
import { getPortfolioPagePath } from "#components/PortfolioPage/portfolioPageRoute"
import { CLASS_GROUPS } from "#constants/classOptions"
import { useUserContext } from "#contexts/UserContextProvider"
import { ClassConstructor } from "#customTypes/composedTypes/classTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentPortfolioConstructor } from "#customTypes/composedTypes/portfolioTypes"
import { AddStudentFormValues } from "#customTypes/composedTypes/studentTypes"
import { Uuid } from "#customTypes/globalTypes"
import { useGetOneStudent } from "#hooks/jotai/useGetStudents"
import { useOneDoc, useSomeDocs } from "#hooks/jotai/useSubscription"

/**
 * "View Portfolio" entry point for a known student id. Renders nothing unless
 * the student is in a portfolio class or already has a portfolio doc — the
 * latter keeps alums/former portfolio students from losing access to existing
 * work. Always runs `ensurePortfolioExists` before navigating, so the portfolio
 * doc and its artwork backfill are in place by the time the page reads them.
 * Navigates to the full-page portfolio (JARVIS-126), carrying the origin
 * pathname so the page's breadcrumb leads back here.
 */
export const ViewPortfolioButtonForStudent = ({
  studentId,
}: {
  studentId: Uuid | undefined
}) => {
  const student = useGetOneStudent(studentId)
  const { currentUserId } = useUserContext()
  const navigate = useNavigate()
  const location = useLocation()

  const classIds = useMemo(() => {
    return uniq(Object.keys(student?.attendance ?? {})).sort()
  }, [student?.attendance])

  const classes = useSomeDocs<ClassConstructor>(
    classAtoms,
    DOC_TYPES.CLASSES,
    classIds
  )

  const isInPortfolioClass = useMemo(
    () => (classes ?? []).some((c) => c.group === CLASS_GROUPS.PORTFOLIO),
    [classes]
  )

  const existingPortfolio = useOneDoc<StudentPortfolioConstructor>(
    portfolioAtoms,
    DOC_TYPES.PORTFOLIOS,
    student?.id ?? null
  )

  const [isOpeningPortfolio, setIsOpeningPortfolio] = useState(false)

  const handleOpenPortfolio = useCallback(async () => {
    if (!student?.id || !currentUserId) return
    setIsOpeningPortfolio(true)
    try {
      await ensurePortfolioExists(student.id, currentUserId as Uuid)
      navigate(getPortfolioPagePath(student.id), {
        state: { from: location.pathname },
      })
    } finally {
      setIsOpeningPortfolio(false)
    }
  }, [student?.id, currentUserId, navigate, location.pathname])

  if (!student || (!isInPortfolioClass && !existingPortfolio)) return null

  return (
    // Row flex so the button trims to content width instead of stretching.
    <Flex>
      <Button
        type="primary"
        icon={<FolderOpenOutlined />}
        onClick={handleOpenPortfolio}
        loading={isOpeningPortfolio}
        data-testid="view-portfolio"
      >
        View Portfolio
      </Button>
    </Flex>
  )
}

/**
 * Form-bound variant: reads the student id from the surrounding AntD form,
 * the same way the profile tabs do.
 */
const ViewPortfolioButton = () => {
  const form = Form.useFormInstance<AddStudentFormValues>()
  const studentId = form.getFieldValue("id") as Uuid | undefined
  return <ViewPortfolioButtonForStudent studentId={studentId} />
}

export default ViewPortfolioButton
